import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { EyeIcon } from '@/components/catalog/icons';
import { Button } from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { ApiError, api } from '@/lib/api';
import { documentCustomerName } from '@/lib/company-names';
import { canPreviewQuotePdf } from '@/lib/quote-pdf-preview';
import type { Quote } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import type { QuotePdfPreviewTarget } from './QuotePdfPreview';

/**
 * "Preview quote PDF" for a DEAL row, phone card or kanban card (owner,
 * 2026-10-25, decision 3 of the quote PDF preview spec).
 *
 * A deal row carries no quotes, so the quotes are fetched on click
 * (`GET /quotes?dealId=`, `perPage: 100`) rather than per row on render:
 *   - 0 quotes  → the popover says so;
 *   - 1 quote   → `onPreview` straight away, no menu;
 *   - several   → a menu, newest first; choosing one calls `onPreview`.
 *
 * ⚠️ **It previews; it fetches no PDF itself.** The caller owns ONE
 * `useQuotePdfPreview` host per page and passes its `open` as `onPreview`.
 *
 * Positioning, focus and keyboard handling copy `RecordCreateMenu`'s portalled
 * row menu (that component navigates and has no loading/empty/error states, so
 * it cannot be reused as is): portalled to the body because every mount sits
 * inside a `TableWrap` whose overflow would clip it, `z-[60]` = the portalled
 * popover rung, right-aligned and clamped so it fits a 320px screen.
 *
 * ⚠️ **Clicks are stopped from bubbling**: a React portal still bubbles through
 * the React tree, and the card variants sit inside clickable cards.
 */
export interface DealQuotesPdfMenuProps {
  dealId: number;
  dealName: string;
  onPreview: (target: QuotePdfPreviewTarget) => void;
  variant: 'row' | 'card';
}

const MENU_WIDTH = 288;
const MENU_BASE_HEIGHT = 48;
const MENU_ITEM_HEIGHT = 48;
const MENU_ITEMS = '[role="menuitem"]';

type MenuState =
  | { kind: 'closed' }
  | { kind: 'empty' }
  | { kind: 'error'; message: string }
  | { kind: 'list'; quotes: Quote[] };

function newestFirst(a: Quote, b: Quote): number {
  const byCreated = Date.parse(b.createdAt) - Date.parse(a.createdAt);
  if (Number.isFinite(byCreated) && byCreated !== 0) return byCreated;
  return b.id - a.id;
}

function targetOf(quote: Quote): QuotePdfPreviewTarget {
  return {
    id: quote.id,
    quoteNumber: quote.quoteNumber,
    subject: quote.subject,
    companyName: quote.company ? documentCustomerName(quote.company) : null,
  };
}

function errorText(caught: unknown): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  return 'The quotes for this deal could not be loaded.';
}

export function DealQuotesPdfMenu({ dealId, dealName, onPreview, variant }: DealQuotesPdfMenuProps) {
  const { ability } = useAuth();
  const menuId = useId();
  const [state, setState] = useState<MenuState>({ kind: 'closed' });
  const [loading, setLoading] = useState(false);
  const [rect, setRect] = useState({ left: 0, top: 0, width: MENU_WIDTH, flip: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const open = state.kind !== 'closed';
  const itemCount = state.kind === 'list' ? state.quotes.length : 1;
  const estimatedHeight = MENU_BASE_HEIGHT + itemCount * MENU_ITEM_HEIGHT;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const width = Math.min(MENU_WIDTH, window.innerWidth - 16);
    const below = window.innerHeight - box.bottom;
    const flip = below < estimatedHeight && box.top > below;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(Math.max(box.right - width, 8), maxLeft);
    setRect({ left, top: flip ? box.top : box.bottom, width, flip });
  }, [estimatedHeight]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const popover = popoverRef.current;
    const first = popover?.querySelector<HTMLElement>(MENU_ITEMS);
    (first ?? popover)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setState({ kind: 'closed' });
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const close = useCallback((returnFocus = true) => {
    setState({ kind: 'closed' });
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const response = await api.listQuotes({ dealId, perPage: 100 }, controller.signal);
      if (controller.signal.aborted) return;
      const quotes = [...response.data].sort(newestFirst);
      const only = quotes.length === 1 ? quotes[0] : undefined;
      if (only) {
        setState({ kind: 'closed' });
        onPreview(targetOf(only));
      } else if (quotes.length === 0) {
        setState({ kind: 'empty' });
      } else {
        setState({ kind: 'list', quotes });
      }
    } catch (caught) {
      if (controller.signal.aborted) return;
      setState({ kind: 'error', message: errorText(caught) });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, [dealId, onPreview]);

  function onTriggerClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      close(false);
      return;
    }
    void load();
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      close(false);
    }
  }

  function onPopoverKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }

    const elements = Array.from(popoverRef.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []);

    if (event.key === 'Tab') {
      // Portalled, so an uncaught Tab would strand focus at the end of the
      // document. With no items there is nothing to move to: close instead.
      if (elements.length === 0) {
        event.preventDefault();
        close();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (elements.length === 0) return;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = elements.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? elements.length - 1
          : event.key === 'ArrowDown'
            ? (current + 1) % elements.length
            : (current - 1 + elements.length) % elements.length;
    elements[next]?.focus();
  }

  function choose(quote: Quote) {
    close(false);
    onPreview(targetOf(quote));
  }

  // Below every hook, so the gate cannot change the hook order.
  if (!canPreviewQuotePdf(ability)) return null;

  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="sm"
        iconOnly
        leadingIcon={<EyeIcon />}
        loading={loading}
        draggable={variant === 'card' ? false : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Preview quote PDF for ${dealName}`}
        tooltip="Preview quote PDF"
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
      />

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            id={menuId}
            tabIndex={-1}
            onKeyDown={onPopoverKeyDown}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            style={{
              position: 'fixed',
              left: rect.left,
              width: rect.width,
              ...(rect.flip ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.top + 4 }),
            }}
            className="z-[60] overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-lg outline-none"
          >
            <p className="truncate px-2 pt-1 pb-1 text-xs font-medium text-fg-muted">{dealName}</p>
            {state.kind === 'empty' && <p className="px-2 pb-2 text-sm text-fg-muted">No quotes on this deal.</p>}
            {state.kind === 'error' && (
              <p role="alert" className="px-2 pb-2 text-sm text-danger">
                {state.message}
              </p>
            )}
            {state.kind === 'list' && (
              <div
                role="menu"
                aria-label={`Quotes on ${dealName}`}
                className="max-h-[min(60vh,20rem)] overflow-y-auto"
              >
                {state.quotes.map((quote) => (
                  <button
                    key={quote.id}
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-fg hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none max-sm:min-h-11"
                    onClick={() => choose(quote)}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{quote.quoteNumber ?? quote.subject}</span>
                      <span className="block truncate text-xs text-fg-muted">{quote.stage?.name ?? '—'}</span>
                    </span>
                    <Money amount={quote.grandTotal} currency={quote.currency} className="shrink-0 text-xs" />
                  </button>
                ))}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
